import { Subject, defer, lastValueFrom, repeat, takeWhile } from 'rxjs';
import { MotionMasterClient } from './motion-master-client';
import { convertParameterVelocityAffectedValue } from './units';
import { DeviceRef, VelocityProfileConfig } from './types';
import { Cia402State, ModesOfOperation } from './cia402';
import { kublerEncoderRegisterValueMeaning } from './os-command';
import { resolveAfter } from './util';
import { isSomanetProductIdInRange } from './product';
import { makeParameterId } from './parameter';

function hex(value?: number): string {
  return `0x${value?.toString(16).toUpperCase()}`;
}

export function parseIntegroEncoderFirmwareVersion(value: number) {
  const str = String(value).padStart(10, '0');
  const major = str.slice(0, 2);
  const minor = str.slice(2, 4);
  const datestamp = str.slice(4);
  const year = datestamp.slice(0, 2);
  const month = datestamp.slice(2, 4);
  const day = datestamp.slice(4, 6);
  return { value, major, minor, datestamp, year, month, day };
}

export class IntegroEncoderCalibration {
  public readonly notifications$ = new Subject<string>();

  private originalVelocityWindow: number = 0; // 0x606D:00

  private originalVelocityWindowTime: number = 0; // 0x606E:00

  private maxMotorSpeed: number = 0;
  private originalMaxMotorSpeed: number = 0; // 0x6080:00

  private quickStopDeceleration: number = 0;
  private originalQuickStopDeceleration: number = 0; // 0x6085:00

  private feedConstantFeed: number = 0;
  private originalFeedConstantFeed: number = 0; // 0x6092:01

  private feedConstantShaftRevolutions: number = 0;
  private originalFeedConstantShaftRevolutions: number = 0; // 0x6092:02

  private feedConstant: number = 0;
  private originalFeedConstant: number = 0; // feed / shaft revolutions

  private siUnitVelocity: number = 0;
  private originalSiUnitVelocity: number = 0; // 0x60A9:00

  private readonly ccwVelocityProfile: VelocityProfileConfig = {
    target: 3000, // RPM
    acceleration: 6000, // RPM/s
    deceleration: 6000, // RPM/s
  };

  private readonly cwVelocityProfile: VelocityProfileConfig = {
    target: -3000, // RPM,
    acceleration: 6000, // RPM/s
    deceleration: 6000, // RPM/s
  };

  constructor(private client: MotionMasterClient, public readonly deviceRef: DeviceRef) {}

  async start(debug = false): Promise<void> {
    // This is a workaround for cases where the response from the previous OS command was not read, causing the next OS command to fail until the response is properly processed.
    // Failing to read the response after running an OS command, before the command succeeds or fails, is a bug in the OS command handling implementation, likely in Motion Master.
    try {
      const response = await lastValueFrom(this.client.request.readOsCommandResponse(this.deviceRef));
      if (response.request === 'started' || response.request === 'running') {
        this.notifications$.next(
          `The OS command response is still being processed: ${response.request}. Aborting the calibration procedure.`
        );
        return;
      }
    } catch (err: unknown) {
      if (err instanceof Error) {
        this.notifications$.next(
          `Error reading the OS command response at the beginning: ${err.message}. Continuing with the calibration procedure.`
        );
      }
    }

    // The calibration procedure should be performed after the drive is fully commissioned, and thus, the drive should be fully operational in Velocity mode.
    // Tuning of the control loop should be done by the user.
    this.notifications$.next(`Validating the device (${this.deviceRef}) before starting the encoder calibration.`);
    await this.validateDevice();

    await this.updateVelocityParameterValues();

    // Retrieve the encoder firmware version and display it.
    let osCommandResponse = await lastValueFrom(
      this.client.request.runKublerEncoderRegisterCommunicationOsCommand(
        this.deviceRef,
        0,
        0x04,
        4,
        0,
        10000,
        1000,
        false
      )
    );
    const encoderFirmwareVersion = parseIntegroEncoderFirmwareVersion(osCommandResponse.kublerRegisterValue);
    this.notifications$.next(
      `The firmware version of the integrated encoder (register 0x04) is: Major=${encoderFirmwareVersion.major}, Minor=${encoderFirmwareVersion.minor}, Date=${encoderFirmwareVersion.datestamp}, Value=${encoderFirmwareVersion.value}.`
    );

    try {
      await this.client.request.resetFault(this.deviceRef);
    } catch {} // ignore

    // Retrieve the encoder configuration index assigned to Port 1.
    const encoder1ConfigurationSensorPort = await this.client.request.upload(this.deviceRef, 0x2110, 1);
    const encoderConfigurationIndex = encoder1ConfigurationSensorPort === 1 ? 0x2110 : 0x2112;

    this.notifications$.next(
      `Setting the integrated encoder sampling frequency to 16 kHz (${hex(encoderConfigurationIndex)}:28 = 1).`
    );
    await this.client.request.download(this.deviceRef, encoderConfigurationIndex, 28, 1);
    await resolveAfter(200);

    await this.client.request.forceOnDemandParametersUpdate(this.deviceRef);

    this.notifications$.next('Switching the source of the velocity to the value provided by the encoder.');
    await lastValueFrom(
      this.client.request.runUseInternalEncoderVelocityOsCommand(this.deviceRef, 1, 10000, 1000, false)
    );
    await resolveAfter(200);

    // Convert the POE calibration velocity profile values and update the device.
    let velocityProfile = await this.setVelocityProfile(this.ccwVelocityProfile, 2);

    this.notifications$.next(
      'Setting modes of operation to Profile velocity mode and transitioning to Operation enabled CiA402 state.'
    );
    await lastValueFrom(
      this.client.request.setModesOfOperation(this.deviceRef, ModesOfOperation.PROFILE_VELOCITY_MODE)
    );
    await this.client.request.transitionToCia402State(this.deviceRef, Cia402State.OPERATION_ENABLED);

    try {
      await this.client.whenTargetReached(this.deviceRef, 500, 5000);
      await this.notifyParameterValue(0x606c, 0); // Velocity actual value
      // Trigger the POA automatic calibration with an OS command.
      this.notifications$.next('Triggering automatic calibration of the POA by setting register 0x25 to 0x09.');
      await this.writeEncoderRegisterValueAndValidateResponse(0x25, 0x09);
      await resolveAfter(500);
    } catch (_err: unknown) {
      this.client.request.quickStop(this.deviceRef).catch();
      this.notifications$.next(
        `Target velocity ${velocityProfile.target} can't be reached. Aborting the calibration procedure.`
      );
      this.revertVelocityParameterValues();
      return;
    }

    this.notifications$.next(
      'Polling the encoder register 0x24 until its value equals 0x11 or until 8 seconds have passed.'
    );

    // Read the value of the encoder register 0x24 until its value equals 0x11 or until 8 seconds have passed.
    osCommandResponse = await lastValueFrom(
      defer(() =>
        this.client.request
          .runKublerEncoderRegisterCommunicationOsCommand(this.deviceRef, 0, 0x24, 1, 0, 10000, 1000, false)
          .pipe(
            repeat({ count: 16, delay: 500 }),
            takeWhile((osCommandResponse) => {
              if (debug) {
                this.notifications$.next(
                  `Value read from the encoder register 0x24 is ${hex(osCommandResponse.kublerRegisterValue)}.`
                );
              }
              return osCommandResponse?.kublerRegisterValue !== 0x11;
            }, true)
          )
      )
    );

    if (osCommandResponse.request === 'failed') {
      this.notifications$.next(
        `Reading the value of register 0x24 failed. ${osCommandResponse.errorName} (${osCommandResponse.errorCode}): ${osCommandResponse.errorDescription}`
      );
    } else if (osCommandResponse.request === 'succeeded') {
      this.notifications$.next(
        `The last value retrieved from register 0x24 is ${osCommandResponse.kublerRegisterValue}: ${
          kublerEncoderRegisterValueMeaning[0x24][osCommandResponse.kublerRegisterValue]
        }`
      );
    }

    if (osCommandResponse.kublerRegisterValue !== 0x11) {
      this.client.request.quickStop(this.deviceRef).catch();
      this.notifications$.next('The analogue gains calibration did not converge. Please repeat the procedure.');
      this.revertVelocityParameterValues();
      return;
    }

    await resolveAfter(500);

    // Lookup table learning.

    this.notifications$.next('Running the motor at a constant speed in one direction.');

    // Run the motor at constant speed in one direction.

    try {
      await this.client.whenTargetReached(this.deviceRef, 500, 5000);
      await this.notifyParameterValue(0x606c, 0); // Velocity actual value
      this.notifications$.next('Writing 0x00C2 to the encoder register 0x50.');
      await this.writeEncoderRegisterValueAndValidateResponse(0x50, 0x00c2, 2);
      await resolveAfter(500);
    } catch (_err: unknown) {
      this.client.request.quickStop(this.deviceRef).catch();
      this.notifications$.next(
        `Target velocity ${velocityProfile.target} can't be reached. Aborting the calibration procedure.`
      );
      this.revertVelocityParameterValues();
      return;
    }

    // Read the value of the encoder register 0x52 until its value satisfies the following condition (bit 6 = 1) & (bit 8 = 0) or until 8 seconds have passed.
    osCommandResponse = await lastValueFrom(
      defer(() =>
        this.client.request
          .runKublerEncoderRegisterCommunicationOsCommand(this.deviceRef, 0, 0x52, 2, 0, 10000, 1000, false)
          .pipe(
            repeat({ count: 16, delay: 500 }),
            takeWhile((osCommandResponse) => {
              if (debug) {
                this.notifications$.next(
                  `Value read from the encoder register 0x52 is ${hex(osCommandResponse.kublerRegisterValue)}.`
                );
              }
              return (osCommandResponse?.kublerRegisterValue & 0b101000000) !== 0x40;
            }, true)
          )
      )
    );

    if (osCommandResponse.request === 'failed') {
      this.notifications$.next(
        `Reading the value of register 0x52 failed. ${osCommandResponse.errorName} (${osCommandResponse.errorCode}): ${osCommandResponse.errorDescription}`
      );
    } else if (osCommandResponse.request === 'succeeded') {
      this.notifications$.next(
        `The last value retrieved from register 0x52 is ${hex(osCommandResponse.kublerRegisterValue)}`
      );
    }

    await this.client.request.download(this.deviceRef, 0x60ff, 0, 0);
    await resolveAfter(200);

    this.notifications$.next('Reversing the rotation direction.');

    // Reverse the rotation direction.

    try {
      const [ec1, ec2] = await this.notifyExtendedErrorStatusAndGetErrorCodes();
      if (((ec1 >> 16) & 0xff) === 0x0f || ((ec2 >> 16) & 0xff) === 0x0f) {
        this.notifications$.next('There was an error in recording the correction table. Please restart the procedure.');
        this.revertVelocityParameterValues();
        return;
      }
    } catch (err) {
      if (err instanceof Error) {
        this.notifications$.next(`Failed to notify the extended error status: ${err.message}`);
      }
    }

    velocityProfile = await this.setVelocityProfile(this.cwVelocityProfile, 2);

    try {
      await this.client.whenTargetReached(this.deviceRef, 500, 5000);
      await this.notifyParameterValue(0x606c, 0); // Velocity actual value
      // Trigger the POA automatic calibration with an OS command.
      this.notifications$.next('Writing 0x00C2 to the encoder register 0x50.');
      await this.writeEncoderRegisterValueAndValidateResponse(0x50, 0x00c2, 2);
      await resolveAfter(500);
    } catch (_err: unknown) {
      this.client.request.quickStop(this.deviceRef).catch();
      this.notifications$.next(
        `Target velocity ${velocityProfile.target} can't be reached. Aborting the calibration procedure.`
      );
      this.revertVelocityParameterValues();
      return;
    }

    // Read the value of the encoder register 0x52 until its value satisfies the following condition (bit 6 = 1) & (bit 8 = 0) or until 8 seconds have passed.
    osCommandResponse = await lastValueFrom(
      defer(() =>
        this.client.request
          .runKublerEncoderRegisterCommunicationOsCommand(this.deviceRef, 0, 0x52, 2, 0, 10000, 1000, false)
          .pipe(
            repeat({ count: 16, delay: 500 }),
            takeWhile((osCommandResponse) => {
              if (debug) {
                this.notifications$.next(
                  `Value read from the encoder register 0x52 is ${hex(osCommandResponse.kublerRegisterValue)}.`
                );
              }
              return (osCommandResponse?.kublerRegisterValue & 0b101000000) !== 0x40;
            }, true)
          )
      )
    );

    if (osCommandResponse.request === 'failed') {
      this.notifications$.next(
        `Reading the value of register 0x52 failed. ${osCommandResponse.errorName} (${osCommandResponse.errorCode}): ${osCommandResponse.errorDescription}`
      );
    } else if (osCommandResponse.request === 'succeeded') {
      this.notifications$.next(
        `The last value retrieved from register 0x52 is ${hex(osCommandResponse.kublerRegisterValue)}`
      );
    }

    await this.client.request.download(this.deviceRef, 0x60ff, 0, 0);
    await resolveAfter(200);

    try {
      const [ec1, ec2] = await this.notifyExtendedErrorStatusAndGetErrorCodes();
      if (((ec1 >> 16) & 0xff) === 0x0f || ((ec2 >> 16) & 0xff) === 0x0f) {
        this.notifications$.next('There was an error in recording the correction table. Please restart the procedure.');
        this.revertVelocityParameterValues();
        return;
      }
      this.notifications$.next('Saving the correction table by writing 0x00D0 to the encoder register 0x50.');
      await this.writeEncoderRegisterValueAndValidateResponse(0x50, 0x00d0, 2);
    } catch (err) {
      if (err instanceof Error) {
        this.notifications$.next(`Failed to notify the extended error status: ${err.message}`);
      }
    }

    await resolveAfter(500);

    this.notifications$.next('Sending a Quick Stop request to the device.');
    this.client.request.quickStop(this.deviceRef).catch();

    await this.revertVelocityParameterValues();

    this.notifications$.next('The encoder calibration has been successfully completed.');
  }

  /**
   * Validate device.
   */
  async validateDevice(): Promise<void> {
    const device = await lastValueFrom(this.client.request.resolveDevice(this.deviceRef));

    // The device must exist.
    if (!device) {
      throw new Error('No device was found when starting the encoder calibration.');
    }

    // The device must be Integro.
    if (
      !isSomanetProductIdInRange(device.hardwareDescription, 'Integro') &&
      !isSomanetProductIdInRange(device.hardwareDescription, 'ACTILINK-S')
    ) {
      throw new Error('The device must be an Integro or ACTILINK-S device for the encoder calibration.');
    }

    // The encoder configuration, which is assigned to port 1, must be of type Internal encoder (5).
    const encoder1ConfigurationSensorPort = await this.client.request.upload(this.deviceRef, 0x2110, 1);
    let encoderSource = 1;
    if (encoder1ConfigurationSensorPort === 1) {
      const encoder1ConfigurationType = await this.client.request.upload(this.deviceRef, 0x2110, 2);
      if (encoder1ConfigurationType !== 5) {
        throw new Error('Encoder configuration 1, which is assigned to port 1, must be of type Internal encoder (5).');
      }
    } else {
      const encoder2ConfigurationSensorPort = await this.client.request.upload(this.deviceRef, 0x2112, 1);
      if (encoder2ConfigurationSensorPort === 1) {
        const encoder2ConfigurationType = await this.client.request.upload(this.deviceRef, 0x2112, 2);
        if (encoder2ConfigurationType !== 5) {
          throw new Error(
            'Encoder configuration 2, which is assigned to port 1, must be of type Internal encoder (5).'
          );
        }
        encoderSource = 2;
      } else {
        throw new Error('No encoder configuration is assigned to port 1.');
      }
    }

    // The internal encoder must have a commutation function.
    const torqueControllerEncoderSource = await this.client.request.upload(this.deviceRef, 0x2010, 12);
    if (torqueControllerEncoderSource !== encoderSource) {
      throw new Error('The internal encoder configuration must have a commutation function.');
    }

    // The internal encoder must have a velocity function.
    const velocityControllerEncoderSource = await this.client.request.upload(this.deviceRef, 0x2011, 5);
    if (velocityControllerEncoderSource !== encoderSource) {
      throw new Error('The internal encoder configuration must have a velocity function.');
    }

    // The commutation offset must be set.
    const commutationOffsetState = await this.client.request.upload(this.deviceRef, 0x2009, 1);
    if (commutationOffsetState !== 2) {
      throw new Error('The commutation offset state must be valid before starting the encoder calibration.');
    }

    // The velocity controller must be tuned.
    const velocityControllerKp = await this.client.request.upload(this.deviceRef, 0x2011, 1);
    if (velocityControllerKp <= 0) {
      throw new Error('Velocity controller must be tuned before starting the encoder calibration.');
    }
  }

  /**
   * Set velocity profile.
   */
  async setVelocityProfile(
    velocityProfileConfig: VelocityProfileConfig,
    accelerationConstant = 2
  ): Promise<VelocityProfileConfig> {
    const config: VelocityProfileConfig = { ...velocityProfileConfig }; // make a copy

    // The velocity target value should not exceed the maximum motor speed.
    // If it's higher than the maximum motor speed, set it to 95% of the maximum motor speed and update acceleration and deceleration accordingly.
    if (Math.abs(config.target) > this.maxMotorSpeed) {
      const sgn = config.target < 0 ? -1 : 1;
      config.target = sgn * this.maxMotorSpeed * 0.95;
      config.acceleration = Math.abs(config.target) * accelerationConstant;
      config.deceleration = Math.abs(config.target) * accelerationConstant;
    }

    // Convert the velocity profile values using the user-configured SI unit velocity and Feed constant.
    config.target = convertParameterVelocityAffectedValue(
      0x60ff,
      0,
      config.target,
      0x00b44700,
      this.siUnitVelocity,
      0xffffffff,
      this.feedConstantFeed
    );
    config.acceleration = convertParameterVelocityAffectedValue(
      0x6083,
      0,
      config.acceleration,
      0x00b44700,
      this.siUnitVelocity,
      0xffffffff,
      this.feedConstantFeed
    );
    config.deceleration = convertParameterVelocityAffectedValue(
      0x6084,
      0,
      config.deceleration,
      0x00b44700,
      this.siUnitVelocity,
      0xffffffff,
      this.feedConstantFeed
    );

    // Update the velocity target, and profile acceleration and deceleration values.
    this.notifications$.next(
      `Setting velocity target value (0x60FF:00) to ${config.target}, with the velocity profile acceleration (0x6083:00) set to ${config.acceleration} and deceleration (0x6084:00) set to ${config.deceleration}.`
    );
    await lastValueFrom(
      this.client.request.setParameterValues([
        [this.deviceRef, 0x6083, 0, config.acceleration], // 0x6083: Profile acceleration
        [this.deviceRef, 0x6084, 0, config.deceleration], // 0x6084: Profile deceleration
        [this.deviceRef, 0x6086, 0, 0], // 0x6086: Motion profile type, 0 is Linear ramp (trapezoidal profile)
        [this.deviceRef, 0x60ff, 0, config.target], // 0x60FF: Target velocity
      ])
    );

    return config;
  }

  /**
   * Write encoder register value and validate response.
   */
  async writeEncoderRegisterValueAndValidateResponse(
    address: number,
    value: number,
    length: number = 1
  ): Promise<void> {
    let osCommandResponse = await lastValueFrom(
      this.client.request.runKublerEncoderRegisterCommunicationOsCommand(
        this.deviceRef,
        1,
        address,
        length,
        value,
        10000,
        1000,
        false
      )
    );

    if (osCommandResponse.kublerRegisterValue !== value) {
      throw new Error(`Unable to set the encoder register ${hex(address)} value to ${hex(value)}.`);
    }
  }

  async notifyParameterValue(index: number, subindex: number) {
    const id = makeParameterId(index, subindex);
    const value = await this.client.request.upload(this.deviceRef, index, subindex);
    this.notifications$.next(`Value of ${id} is ${value}.`);
  }

  async notifyExtendedErrorStatusAndGetErrorCodes() {
    const ec1Response = await lastValueFrom(
      this.client.request.runKublerEncoderRegisterCommunicationOsCommand(
        this.deviceRef,
        0x00,
        0x66,
        0x04,
        0,
        10000,
        1000,
        false
      )
    );
    this.notifications$.next(`The value of register 0x66 (Error code 1) is ${hex(ec1Response.kublerRegisterValue)}.`);

    const ec2Response = await lastValueFrom(
      this.client.request.runKublerEncoderRegisterCommunicationOsCommand(
        this.deviceRef,
        0x00,
        0x6a,
        0x04,
        0,
        10000,
        1000,
        false
      )
    );
    this.notifications$.next(`The value of register 0x6A (Error code 2) is ${hex(ec2Response.kublerRegisterValue)}.`);

    this.notifications$.next(`Clearing the encoder errors by writing the value 0x02 to register 0x61.`);
    await lastValueFrom(
      this.client.request.runKublerEncoderRegisterCommunicationOsCommand(
        this.deviceRef,
        0x01,
        0x61,
        0x01,
        0x02,
        10000,
        1000,
        false
      )
    );

    return [ec1Response.kublerRegisterValue, ec2Response.kublerRegisterValue];
  }

  async notifyEncoderSpeeds() {
    const ccwStartSpeed = await lastValueFrom(
      this.client.request.runKublerEncoderRegisterCommunicationOsCommand(
        this.deviceRef,
        0x00,
        0x5a,
        0x02,
        0,
        10000,
        1000,
        false
      )
    );
    this.notifications$.next(
      `The value of register 0x5A (CCW start speed) is ${hex(ccwStartSpeed.kublerRegisterValue)}.`
    );

    const ccwEndSpeed = await lastValueFrom(
      this.client.request.runKublerEncoderRegisterCommunicationOsCommand(
        this.deviceRef,
        0x00,
        0x5c,
        0x02,
        0,
        10000,
        1000,
        false
      )
    );
    this.notifications$.next(`The value of register 0x5C (CCW end speed) is ${hex(ccwEndSpeed.kublerRegisterValue)}.`);

    const cwStartSpeed = await lastValueFrom(
      this.client.request.runKublerEncoderRegisterCommunicationOsCommand(
        this.deviceRef,
        0x00,
        0x56,
        0x02,
        0,
        10000,
        1000,
        false
      )
    );
    this.notifications$.next(
      `The value of register 0x56 (CW start speed) is ${hex(cwStartSpeed.kublerRegisterValue)}.`
    );

    const cwEndSpeed = await lastValueFrom(
      this.client.request.runKublerEncoderRegisterCommunicationOsCommand(
        this.deviceRef,
        0x00,
        0x58,
        0x02,
        0,
        10000,
        1000,
        false
      )
    );
    this.notifications$.next(`The value of register 0x58 (CW end speed) is ${hex(cwEndSpeed.kublerRegisterValue)}.`);
  }

  async updateVelocityParameterValues() {
    this.originalVelocityWindow = await this.client.request.upload(this.deviceRef, 0x606d, 0);
    this.originalVelocityWindowTime = await this.client.request.upload(this.deviceRef, 0x606e, 0);
    this.originalMaxMotorSpeed = await this.client.request.upload(this.deviceRef, 0x6080, 0);
    this.originalQuickStopDeceleration = await this.client.request.upload(this.deviceRef, 0x6085, 0);
    this.originalFeedConstantFeed = await this.client.request.upload(this.deviceRef, 0x6092, 1);
    this.originalFeedConstantShaftRevolutions = await this.client.request.upload(this.deviceRef, 0x6092, 2);
    this.originalFeedConstant = this.originalFeedConstantFeed / this.originalFeedConstantShaftRevolutions;
    this.originalSiUnitVelocity = await this.client.request.upload(this.deviceRef, 0x60a9, 0);

    let velocityWindow = convertParameterVelocityAffectedValue(
      0x606d,
      0,
      120,
      0x00b44700,
      this.originalSiUnitVelocity,
      0xffffffff,
      this.originalFeedConstantFeed
    );
    const velocityWindowTime = 500;
    const switchToRpm = velocityWindow > 65535;

    if (switchToRpm) {
      this.notifications$.next(
        'The user defined velocity units interfere with the maximum value of the calibration profile Velocity Window. Temporarily switching all units to RPM.'
      );

      velocityWindow = 120;

      this.siUnitVelocity = 0x00b44700;
      this.feedConstantFeed = 0xffffffff;
      this.feedConstantShaftRevolutions = 1;
      this.feedConstant = this.feedConstantFeed / this.feedConstantShaftRevolutions;

      this.maxMotorSpeed = convertParameterVelocityAffectedValue(
        0x6080,
        0,
        this.originalMaxMotorSpeed,
        this.originalSiUnitVelocity,
        this.siUnitVelocity,
        this.originalFeedConstant,
        this.feedConstant
      );
      this.notifications$.next(`Setting the Max motor speed (0x6080:00) to ${this.maxMotorSpeed}.`);
      await this.client.request.download(this.deviceRef, 0x6080, 0, this.maxMotorSpeed);

      this.quickStopDeceleration = convertParameterVelocityAffectedValue(
        0x6085,
        0,
        this.originalQuickStopDeceleration,
        this.originalSiUnitVelocity,
        this.siUnitVelocity,
        this.originalFeedConstant,
        this.feedConstant
      );
      this.notifications$.next(`Setting the Quick stop deceleration (0x6085:00) to ${this.quickStopDeceleration}.`);
      await this.client.request.download(this.deviceRef, 0x6085, 0, this.quickStopDeceleration);
    } else {
      // use the original velocity values
      this.siUnitVelocity = this.originalSiUnitVelocity;
      this.feedConstantFeed = this.originalFeedConstantFeed;
      this.feedConstantShaftRevolutions = this.originalFeedConstantShaftRevolutions;
      this.feedConstant = this.originalFeedConstant;
      this.maxMotorSpeed = this.originalMaxMotorSpeed;
      this.quickStopDeceleration = this.originalQuickStopDeceleration;
    }

    this.notifications$.next(`Setting the Velocity window (0x606D:00) to ${velocityWindow}.`);
    await this.client.request.download(this.deviceRef, 0x606d, 0, velocityWindow);

    this.notifications$.next(`Setting the Velocity window time (0x606E:00) to ${velocityWindowTime}.`);
    await this.client.request.download(this.deviceRef, 0x606e, 0, velocityWindowTime);

    if (switchToRpm) {
      this.notifications$.next(`Setting the SI unit velocity (0x60A9:00) to ${hex(this.siUnitVelocity)}.`);
      await this.client.request.download(this.deviceRef, 0x60a9, 0, this.siUnitVelocity);

      this.notifications$.next(`Setting the Feed constant Feed (0x6092:01) to ${this.feedConstantFeed}.`);
      await this.client.request.download(this.deviceRef, 0x6092, 1, this.feedConstantFeed);

      this.notifications$.next(
        `Setting the Feed constant Shaft revolutions (0x6092:02) to ${this.feedConstantShaftRevolutions}.`
      );
      await this.client.request.download(this.deviceRef, 0x6092, 2, this.feedConstantShaftRevolutions);

      await this.client.request.forceOnDemandParametersUpdate(this.deviceRef);
    }
  }

  async revertVelocityParameterValues() {
    this.notifications$.next(`Reverting the SI unit velocity (0x60A9:00) to  ${hex(this.originalSiUnitVelocity)}.`);
    await this.client.request.download(this.deviceRef, 0x60a9, 0, this.originalSiUnitVelocity);

    this.notifications$.next(`Reverting the Feed constant Feed (0x6092:01) to ${this.originalFeedConstantFeed}.`);
    await this.client.request.download(this.deviceRef, 0x6092, 1, this.originalFeedConstantFeed);

    this.notifications$.next(
      `Reverting the Feed constant Shaft revolutions (0x6092:02) to ${this.originalFeedConstantShaftRevolutions}.`
    );
    await this.client.request.download(this.deviceRef, 0x6092, 2, this.originalFeedConstantShaftRevolutions);

    await this.client.request.forceOnDemandParametersUpdate(this.deviceRef);

    this.notifications$.next(`Reverting the Max motor speed (0x6080:00) to ${this.originalMaxMotorSpeed}.`);
    await this.client.request.download(this.deviceRef, 0x6080, 0, this.originalMaxMotorSpeed);

    this.notifications$.next(
      `Reverting the Quick stop deceleration (0x6085:00) to ${this.originalQuickStopDeceleration}.`
    );
    await this.client.request.download(this.deviceRef, 0x6085, 0, this.originalQuickStopDeceleration);

    this.notifications$.next(`Reverting the Velocity window (0x606D:00) to ${this.originalVelocityWindow}.`);
    await this.client.request.download(this.deviceRef, 0x606d, 0, this.originalVelocityWindow);

    this.notifications$.next(`Reverting the Velocity window time (0x606E:00) to ${this.originalVelocityWindowTime}.`);
    await this.client.request.download(this.deviceRef, 0x606e, 0, this.originalVelocityWindowTime);
  }
}
